import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

import {
  determinePlatformType,
  isFastDraftDeployment,
} from "src/reusable/Functions";

import routes from "../routes";

// FastDraft deployments brand their own tab in index.html; the title effect
// must not overwrite that with Nuclearn's. Read per call rather than at module
// load, so a config.js parsed after this module still brands the title —
// `Functions.tsx` captures PLATFORM_TYPE once, so only the IS_FASTDRAFT half
// is genuinely per call.
const brand = () => (isFastDraftDeployment() ? "FastDraft" : "Nuclearn");

// The assistant's product label, keyed off the same isFastDraftDeployment()
// as the sidebar and header logos so an IS_FASTDRAFT-only config does not
// title "FastDraft" over an "AtomAssist" header. The sidebar nav *labels*
// still read determinePlatformType() (`src/containers/_nav.jsx`), so such a
// config keeps "AtomAssist" there.
const productLabel = () =>
  isFastDraftDeployment() ? "FastDraft" : determinePlatformType();

const publicTitles: Record<string, string> = {
  "/login": "Sign in",
  "/password-reset-request": "Reset password",
  "/password-reset": "Choose a new password",
  "/register": "Create account",
  "/support-login": "Support sign in",
  "/404": "Page not found",
  "/500": "Something went wrong",
};

// Page labels come from the `name`s in routes.jsx. These override names that
// are missing, generic, or repeat the product label, keyed by route path.
// `undefined` means the page shows only its product (or brand) title.
export const routeLabelOverrides: Record<string, string | undefined> = {
  "/assistant": undefined,
  "/assistant/chat/:dialogueId": "Conversation",
  "/assistant/recents": "Recent conversations",
  "/products": "Product directory",
  "/userprofile": "Your profile",
  "/oauth/new": "OAuth",
  "/oauth/provider": "OAuth",
  "/fifty-fifty-nine/records": "Records",
  "/fifty-fifty-nine/chat/:dialogueId": "Conversation",
  "/fifty-fifty-nine/recents": "Recent conversations",
  "/parts/chat/:parts_ai_run_id/:dialogueId": "Conversation",
  "/parts/recents": "Recent conversations",
  "/proposal-generation/chat/:proposal_generation_run_id/:dialogueId":
    "Conversation",
  "/proposal-generation/recents": "Recent conversations",
  "/proposal-generation/runs": "Runs",
  "/vnv-assist/chat/:vnv_run_id/:dialogueId": "Conversation",
  "/vnv-assist/recents": "Recent conversations",
  "/observation-program/chat": "Chat",
  "/observation-program/chat/:id": "Conversation",
  "/products/equipment-ai/integrations": "Integrations",
  "/products/equipment-ai/onboarding": "Onboarding",
  "/products/equipment-ai/recents": "Recent conversations",
  "/products/equipment-ai/schedules": "Schedules",
  // Back-compat redirects: the path params are ids, not page names.
  "/products/equipment-ai/asset/:afElementId/location/:locationId/chat/:dialogueId":
    undefined,
  "/products/equipment-ai/asset/:afElementId/location/:locationId": undefined,
  "/products/equipment-ai/asset/:afElementId/chat/:dialogueId": undefined,
  "/products/equipment-ai/asset/:afElementId": undefined,
  "/products/equipment-ai/location/:locationId/chat/:dialogueId": undefined,
  "/products/equipment-ai/chat/:dialogueId": undefined,
};

const productContexts = [
  { prefix: "/fifty-fifty-nine", label: "50.59 Evaluation" },
  { prefix: "/products/equipment-ai", label: "Equipment AI" },
  { prefix: "/products/capitalizer", label: "Capitalizer" },
  { prefix: "/products/regulatory-ai", label: "Regulatory AI" },
  { prefix: "/products/safety-analytics", label: "Safety Analytics" },
  { prefix: "/products/scoring-wizard", label: "Dataset Scoring" },
  { prefix: "/products/project-genius-wizard", label: "Project Genius" },
  { prefix: "/products/cap-ai", label: "CAP AI" },
  { prefix: "/products/project-genius", label: "Project Genius" },
  { prefix: "/products/outage-optimizer", label: "Outage Optimizer" },
  { prefix: "/proposal-generation", label: "Proposal Generation" },
  { prefix: "/document-intelligence", label: "Document Intelligence" },
  { prefix: "/observation-program", label: "Observation Program" },
  { prefix: "/vnv-assist", label: "V&V Assist" },
  { prefix: "/parts", label: "Parts AI" },
];

// Fallback for URLs no named route matches: label by the first path segment.
const sectionLabels: Record<string, string> = {
  admin: "Administration",
  "atom-results": "AtomResults",
  buckets: "Buckets",
  datasets: "Datasets",
  documents: "Documents",
  evaluations: "Evaluations",
  groups: "Groups",
  jobs: "Jobs",
  modeldetails: "Model details",
  models: "Models",
  oauth: "OAuth",
  reports: "Reports",
  users: "Users",
};

// The label maps are object literals, so a typed URL like `/constructor` must
// not resolve to an Object.prototype member.
const lookup = <T,>(map: Record<string, T>, key: string) =>
  Object.hasOwn(map, key) ? map[key] : undefined;

// On FastDraft the product and the brand are the same word, so drop the
// repeat instead of rendering "FastDraft · FastDraft".
const formatTitle = (...segments: Array<string | undefined>) => {
  const parts = [...segments.filter(Boolean), brand()] as string[];
  return parts
    .filter((part, index) => parts.indexOf(part) === index)
    .join(" · ");
};

const isWithin = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

// Router and URLSearchParams hand us decoded values already; a stray '%'
// would make a second decode throw inside the title effect.
const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const humanizeSlug = (slug: string) =>
  safeDecode(slug)
    .replace(/[-_\s]+/g, " ")
    .trim()
    .replace(/(^|\s)(\S)/g, (_, space, letter) => space + letter.toUpperCase());

type RouteEntry = { path?: string | string[]; exact?: boolean; name?: string };

const segmentCount = (path: string) => path.split("/").filter(Boolean).length;
const paramCount = (path: string) => (path.match(/\/:/g) || []).length;

const compareScores = (a: number[], b: number[]) => {
  const index = a.findIndex((value, i) => value !== b[i]);
  return index === -1 ? 0 : a[index] - b[index];
};

// The most specific route that matches: an exact match first, then the
// longest path, then the one with the fewest params.
const matchRoute = (pathname: string) => {
  let best: { path: string; name?: string; score: number[] } | undefined;
  for (const route of routes as RouteEntry[]) {
    if (!route.path) continue;
    const match = matchPath(pathname, {
      path: route.path,
      exact: route.exact,
    });
    if (!match) continue;
    const score = [
      match.isExact ? 1 : 0,
      segmentCount(match.path),
      -paramCount(match.path),
    ];
    if (!best || compareScores(score, best.score) > 0)
      best = { path: match.path, name: route.name, score };
  }
  return best;
};

const getRouteLabel = (pathname: string) => {
  const route = matchRoute(pathname);
  if (!route) return undefined;
  return Object.hasOwn(routeLabelOverrides, route.path)
    ? routeLabelOverrides[route.path]
    : route.name;
};

const getSectionLabel = (pathname: string) => {
  const [section] = pathname.split("/").filter(Boolean);
  if (!section) return undefined;
  return lookup(sectionLabels, section) || humanizeSlug(section);
};

export const getPageTitle = (pathname: string, search = "") => {
  const normalizedPathname =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const publicTitle = lookup(publicTitles, normalizedPathname);
  if (publicTitle) return formatTitle(publicTitle);

  if (normalizedPathname === "/" || normalizedPathname === "/assistant") {
    const persona = new URLSearchParams(search).get("persona");
    return persona
      ? formatTitle(humanizeSlug(persona), productLabel())
      : formatTitle(`Ask ${productLabel()}`);
  }

  const label = getRouteLabel(normalizedPathname);

  if (isWithin(normalizedPathname, "/assistant")) {
    return formatTitle(label, productLabel());
  }

  const productContext = productContexts.find(({ prefix }) =>
    isWithin(normalizedPathname, prefix),
  );
  if (productContext) {
    const isContextLanding = normalizedPathname === productContext.prefix;
    return formatTitle(
      isContextLanding || label === productContext.label ? undefined : label,
      productContext.label,
    );
  }

  return formatTitle(label || getSectionLabel(normalizedPathname));
};

const PageTitleManager = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = getPageTitle(location.pathname, location.search);
  }, [location.pathname, location.search]);

  return null;
};

export default PageTitleManager;
